import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  type OnDestroy,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FeatureFlagsService } from '../../../services/feature-flags/feature-flags';
import { MetadataService } from '../../../services/metadata/metadata';
import { ReaderNavigatorService } from '../../../services/readium/navigator/reader-navigator';
import { SettingsService } from '../../../services/readium/settings/settings';
import { PageSettingsComponent } from '../page-settings/page-settings';
import { TextSettingsComponent } from '../text-settings/text-settings';

@Component({
  selector: 'nr-reader-settings-tabs',
  imports: [TextSettingsComponent, TranslatePipe, PageSettingsComponent],
  templateUrl: './reader-settings-tabs.html',
  styleUrl: './reader-settings-tabs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ReaderSettingsTabsComponent implements OnDestroy {
  private static readonly ANIMATION_DURATION_MS = 500;
  private static readonly HEIGHT_CHANGE_THRESHOLD = 1;

  private readonly settingsService = inject(SettingsService);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly navigatorService = inject(ReaderNavigatorService);
  private readonly metadataService = inject(MetadataService);
  private readonly el = inject(ElementRef);

  private resizeObserver: ResizeObserver | null = null;
  private lastHeight = 0;
  private currentAnimation: Animation | null = null;
  private isAnimating = false;
  readonly settings = this.settingsService.settings;
  readonly fullSettingsEnabled = this.featureFlagsService.isEnabled('fullSettings');
  readonly canUseTwoColumns = this.navigatorService.canUseTwoColumns;
  readonly isFixedLayout = this.metadataService.isFixedLayout;

  constructor() {
    afterNextRender(() => {
      this.setupHeightTransition();
    });
  }

  changeFontFamily(fontFamily: string | null): void {
    this.settingsService.updateReaderSettings({ fontFamily });
  }

  private setupHeightTransition(): void {
    const nativeElement: unknown = this.el.nativeElement;
    if (!(nativeElement instanceof HTMLElement)) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        if (this.isAnimating) return;

        const [entry] = entries;
        const newHeight = entry.borderBoxSize[0].blockSize;

        if (
          this.lastHeight > 0 &&
          Math.abs(newHeight - this.lastHeight) >
            ReaderSettingsTabsComponent.HEIGHT_CHANGE_THRESHOLD
        ) {
          const fromHeight = this.lastHeight;
          this.lastHeight = newHeight;
          this.isAnimating = true;

          this.currentAnimation?.cancel();
          const animation = nativeElement.animate(
            [{ height: `${fromHeight}px` }, { height: `${newHeight}px` }],
            { duration: ReaderSettingsTabsComponent.ANIMATION_DURATION_MS, easing: 'ease' },
          );
          this.currentAnimation = animation;
          animation.onfinish = (): void => {
            this.currentAnimation = null;
            this.isAnimating = false;
          };
          animation.oncancel = (): void => {
            this.currentAnimation = null;
            this.isAnimating = false;
          };
        } else {
          this.lastHeight = newHeight;
        }
      });
    });

    this.resizeObserver.observe(nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.currentAnimation?.cancel();
  }
}
